import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from '../icon/icon';
import type { SelectOption } from '../select/select';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ui-autocomplete',
  imports: [IconComponent],
  styleUrl: './autocomplete.scss',
  templateUrl: './autocomplete.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
})
export class AutocompleteComponent implements ControlValueAccessor {
  /** Libellé affiché au-dessus du champ. */
  @Input() label = '';

  /** Options proposées (ex. liste d'utilisateurs ou de projets). */
  @Input() options: SelectOption[] = [];

  /** Texte indicatif. */
  @Input() placeholder = 'Rechercher...';

  /** Texte d'aide. */
  @Input() hint = '';

  /** Message d'erreur. */
  @Input() error = '';

  /** Désactive le champ. */
  @Input() disabled = false;

  protected readonly open = signal(false);
  protected readonly query = signal('');
  protected value = '';

  protected readonly filtered = computed(() => {
    const text = this.query().trim().toLowerCase();
    if (!text) {
      return this.options;
    }
    return this.options.filter((option) => option.label.toLowerCase().includes(text));
  });

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  private readonly elementRef = inject(ElementRef);

  writeValue(value: unknown): void {
    this.value = value == null ? '' : String(value);
    const option = this.options.find((item) => item.value === this.value);
    this.query.set(option?.label ?? this.value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.query.set(target.value);
    this.open.set(true);
    this.value = '';
    this.onChange('');
  }

  protected select(option: SelectOption): void {
    this.value = option.value;
    this.query.set(option.label);
    this.open.set(false);
    this.onChange(option.value);
    this.onTouched();
  }

  protected onFocus(): void {
    this.open.set(true);
  }

  protected onBlur(): void {
    // Laisse le temps au clic de l'option de s'enregistrer.
    window.setTimeout(() => this.open.set(false), 150);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    if (!host.contains(event.target as Node)) {
      this.open.set(false);
    }
  }
}
